import { UnknownPageError } from './unknown_page_error'

// Presenter pages
import presenterDashboard from './presenter/dashboard'
import activities from './presenter/activities'
import reports from './presenter/reports'

// Authentication pages
import termsAgreement from './auth/terms_agreement'
import logout from './auth/logout'
import login from './auth/login'
import resetPassword from './auth/reset_password'
import signup from './auth/signup'
import outOfBandComplete from './auth/out_of_band_complete'

// Participant pages
import participantHome from './participant/home'
import participantLegacyVote from './participant/legacy_vote'
import participantHistory from './participant/history'
import participantLiveResponse from './participant/live_response'
import participantStaticResponse from './participant/static_response'
import participantRegistrations from './participant/registrations'
import participantRegister from './participant/register'
import participantRegisterSearch from './participant/register_search'
import participantBecomePresenter from './participant/become_presenter'

// Agave pages
import agaveActivities from './agave/activities'
import agavePresenter from './agave/presenter'

// Profile pages
import profile from './settings/profile'

// Slideware pages
import slideware from './integration/slideware'

// Remote control pages
import presentationRemote from './remote_control/presentation'
import activatedActivity from './remote_control/activated_activity'

// Support pages
import openApplicationTroubleshoot from '../util/open_application_troubleshoot'
import feedback from './support/feedback'
import optOutFeedback from './support/opt_out'

// System pages
import welcome from './welcome'
import notFound from './not_found'
import pleaseWait from './auth/please_wait'

export const systemPages = {
  terms_agreement: {
    handler: termsAgreement,
    userRequired: true
  },
  settings: {
    handler: profile,
    userRequired: true
  },
  login: {
    handler: login,
    userRequired: false
  },
  'log in': {
    handler: login,
    userRequired: false
  },
  logout: {
    handler: logout,
    userRequired: false
  },
  reset_password: {
    handler: resetPassword,
    userRequired: false
  },
  signup: {
    handler: signup,
    userRequired: false
  },
  support: {
    handler: openApplicationTroubleshoot,
    userRequired: false
  },
  feedback: {
    handler: feedback,
    userRequired: false
  },
  'opt-out': {
    handler: optOutFeedback,
    userRequired: true
  },
  welcome: {
    handler: welcome,
    userRequired: true
  },
  not_found: {
    handler: notFound,
    userRequired: false
  }
}

export const pagesByRole = {
  participant: {
    home: {
      handler: participantHome,
      userRequired: false
    },
    legacy_vote: {
      handler: participantLegacyVote,
      userRequired: false
    },
    response_history: {
      handler: participantHistory,
      userRequired: false
    },
    live_response: {
      handler: participantLiveResponse,
      userRequired: false
    },
    static_response: {
      handler: participantStaticResponse,
      userRequired: false
    },
    registrations: {
      userRequired: true,
      handler: participantRegistrations
    },
    register: {
      handler: participantRegister,
      userRequired: false
    },
    register_search: {
      handler: participantRegisterSearch,
      userRequired: false
    },
    present: {
      handler: participantBecomePresenter,
      userRequired: false
    }
  },
  presenter: {
    home: {
      handler: presenterDashboard,
      userRequired: true
    },
    activities: {
      handler: activities,
      userRequired: true
    },
    reports: {
      handler: reports,
      userRequired: true
    },
    remote: {
      handler: presentationRemote,
      userRequired: true
    },
    'presenter-controls': {
      handler: activatedActivity,
      userRequired: true
    },
    slideware: {
      handler: slideware,
      userRequired: (state) => {
        // these 2 are special pages
        if (['modal', 'liveslide_security'].includes(state.appStates.slideware.get('page'))) {
          return false
        }

        return true
      }
    }
  },
  agave: {
    activities: {
      handler: agaveActivities,
      userRequired: true
    },
    presenter: {
      handler: agavePresenter,
      userRequired: true
    }
  },
  embed: {
    blank: {
      handler: false,
      userRequired: false
    },
    live_response: {
      handler: participantLiveResponse,
      userRequired: false
    },
    static_response: {
      handler: participantStaticResponse,
      userRequired: false
    },
    register: {
      handler: participantRegister,
      userRequired: false
    }
  },
  auth: {
    out_of_band_complete: {
      handler: outOfBandComplete,
      userRequired: true
    },
    please_wait: {
      handler: pleaseWait,
      userRequired: false
    }
  }
}

export function getPageConfig (role, page) {
  const pages = Object.assign({}, systemPages, pagesByRole[role])
  if (!pages[page]) throw new UnknownPageError(`Unknown page: ${page}`)
  return pages[page]
}

export async function getContent (state, page, lastPage, pageOptions) {
  // must content to agreement before continuing
  if (page !== 'terms_agreement' && state.get('participant').get('must_consent')) {
    state.changePage('terms_agreement')
    return
  }
  const role = state.get('role')
  const { userRequired, handler } = getPageConfig(role, page)

  let isUserRequired = userRequired
  if (typeof userRequired === 'function') {
    isUserRequired = userRequired.call(this, state)
  }

  let content
  if (handler) {
    // if the page requires a user return the login handler instead
    if (isUserRequired && !state.loggedIn) {
      content = await login.call(this, state, this.options, lastPage, pageOptions)
    } else {
      content = await handler.call(this, state, this.options, lastPage, pageOptions)

      // the state has moved on already
      if (page !== state.get('page')) {
        console.log(`Page changed to ${state.get('page')} while loading ${page}, aborting rendering`)
        return
      }
    }
  }

  state.notifyPageLoaded?.(page)
  // if page is undefined, go back to lastPage
  content && content.on && content.on('navigate', (page, options = {}) => {
    // special pages to switch between participating and presenting
    if (page === 'participate') {
      this.state.set({ role: 'participant' })
      return
    }

    if (!page) {
      // You can only go back to these pages. If it's not in the list,
      // default to activities
      if (['activities', 'reports', 'remote'].includes(lastPage)) {
        page = lastPage
      } else {
        page = state.defaultPage
      }
    }

    state.changePage(page, options)
  })

  return content
}
